// Phase H+1 Mini-Wave 5 · V12 User Command Palette ⌘K.
// V12 Achromatic Strict · italiano puro default.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Search } from "lucide-react";
import { motion } from "framer-motion";

import { flattenUserNavItems } from "@/data/v12_user_nav";
import { useTranslation } from "@/i18n/I18nContext";

function _norm(s) { return (s || "").toLowerCase().trim(); }
function _score(item, query) {
    if (!query) return 1;
    const q = _norm(query);
    const h = _norm(`${item.label} ${item.groupLabel} ${(item.keywords || []).join(" ")}`);
    if (!h.includes(q)) {
        let qi = 0;
        for (let i = 0; i < h.length && qi < q.length; i++) if (h[i] === q[qi]) qi++;
        return qi === q.length ? 10 : 0;
    }
    return h.indexOf(q) === 0 ? 100 : 50;
}

export default function V12UserCommandPalette({ onClose, role = "user" }) {
    const [query, setQuery] = useState("");
    const [cursor, setCursor] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => { inputRef.current?.focus(); }, []);

    const items = useMemo(() => {
        const flat = flattenUserNavItems(role);
        return flat
            .map((it) => ({ ...it, _score: _score(it, query) }))
            .filter((it) => (!query || it._score > 0))
            .sort((a, b) => b._score - a._score)
            .slice(0, 30);
    }, [query, role]);

    useEffect(() => { setCursor(0); }, [query]);

    function onKeyDown(e) {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setCursor((c) => Math.min(c + 1, items.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setCursor((c) => Math.max(0, c - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const sel = items[cursor];
            if (sel && sel.implemented) {
                if (sel.legacyHref) {
                    window.location.assign(sel.legacyHref);
                } else if (sel.to) {
                    navigate(sel.to.split("#")[0]);
                }
                onClose();
            }
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm
                       flex items-start justify-center pt-24"
            onClick={onClose}
            data-testid="v12-user-cmd-overlay"
        >
            <motion.div
                initial={{ y: -8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="w-full max-w-xl mx-4 rounded-lg border v12-border
                           v12-bg shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="v12-user-cmd-palette"
            >
                <div className="flex items-center gap-2 px-3 py-2 border-b v12-border">
                    <Search size={14} className="stroke-zinc-500" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder={t("user_nav.cmd_placeholder") !== "user_nav.cmd_placeholder"
                            ? t("user_nav.cmd_placeholder")
                            : "Vai a sezione, modulo, pagina..."}
                        className="flex-1 bg-transparent text-sm text-neutral-100
                                   placeholder:text-neutral-500 outline-none py-1"
                        data-testid="v12-user-cmd-input"
                    />
                    <kbd className="text-[10px] text-neutral-600 px-1.5 py-0.5
                                    rounded border v12-border">Esc</kbd>
                </div>
                <ul className="max-h-80 overflow-y-auto" data-testid="v12-user-cmd-results">
                    {items.length === 0 && (
                        <li className="px-3 py-6 text-xs text-neutral-500 text-center">
                            {t("common.empty")}
                        </li>
                    )}
                    {items.map((it, i) => {
                        const active = i === cursor;
                        return (
                            <li
                                key={it.id}
                                onMouseEnter={() => setCursor(i)}
                                onClick={() => {
                                    if (!it.implemented) return;
                                    if (it.legacyHref) {
                                        window.location.assign(it.legacyHref);
                                    } else if (it.to) {
                                        navigate(it.to.split("#")[0]);
                                    }
                                    onClose();
                                }}
                                className={`
                                    flex items-center gap-2 px-3 py-2 cursor-pointer
                                    transition-colors duration-150
                                    ${active ? "v12-card" : "bg-transparent"}
                                    ${it.implemented ? "" : "cursor-not-allowed opacity-60"}
                                `}
                                data-testid={`v12-user-cmd-item-${it.id}`}
                            >
                                <span className="text-xs text-neutral-100 truncate">
                                    {it.label}
                                </span>
                                <span className="text-[11px] text-neutral-500 ml-auto">
                                    {it.groupLabel}
                                </span>
                                {!it.implemented && (
                                    <Lock size={10} className="stroke-zinc-700" />
                                )}
                            </li>
                        );
                    })}
                </ul>
                <div className="px-3 py-1.5 border-t v12-border
                                text-[10px] text-neutral-500 flex gap-4">
                    <span>↑↓ naviga</span>
                    <span>⏎ apri</span>
                    <span>Esc chiudi</span>
                </div>
            </motion.div>
        </motion.div>
    );
}
